/**
 * What Stage — the iconic Bonnaroo mainstage with the curved roof and twin
 * jumbotron screens. Built from clean primitives, ~250 lines.
 */

import * as THREE from 'three';
import { type StageHandle, makeScreenQuad } from './StageBase';

export class WhatStage {
  static build(): StageHandle {
    const group = new THREE.Group();
    group.name = 'WhatStage';

    const matSteel = new THREE.MeshStandardMaterial({
      color: '#1a1a20',
      roughness: 0.45,
      metalness: 0.85
    });
    const matTruss = new THREE.MeshStandardMaterial({
      color: '#2a2a32',
      roughness: 0.55,
      metalness: 0.8
    });
    const matDeck = new THREE.MeshStandardMaterial({
      color: '#0e0e14',
      roughness: 0.7,
      metalness: 0.2
    });
    const matScreen = new THREE.MeshBasicMaterial({ color: '#0a0a18' });
    const matScreenFrame = new THREE.MeshStandardMaterial({
      color: '#1c1c22',
      roughness: 0.4,
      metalness: 0.9
    });
    const matTrim = new THREE.MeshStandardMaterial({
      color: '#ff5f6d',
      emissive: '#ff5f6d',
      emissiveIntensity: 1.4,
      metalness: 0.5
    });
    const matLed = new THREE.MeshStandardMaterial({
      color: '#ffc371',
      emissive: '#ffc371',
      emissiveIntensity: 1.6
    });
    const matSpeaker = new THREE.MeshStandardMaterial({
      color: '#1a1a1f',
      roughness: 0.55,
      metalness: 0.6
    });

    /* ── Stage deck ── */
    const deckW = 58;
    const deckD = 22;
    const deckH = 2.4;
    const deck = new THREE.Mesh(new THREE.BoxGeometry(deckW, deckH, deckD), matDeck);
    deck.position.y = deckH / 2;
    deck.castShadow = true;
    deck.receiveShadow = true;
    group.add(deck);

    // Front lip / glow strip
    const lip = new THREE.Mesh(new THREE.BoxGeometry(deckW + 0.4, 0.3, 0.5), matTrim);
    lip.position.set(0, deckH + 0.1, deckD / 2 + 0.2);
    group.add(lip);
    // Side trim glow
    for (const sx of [-1, 1] as const) {
      const sideTrim = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.3, deckD), matTrim);
      sideTrim.position.set(sx * (deckW / 2 + 0.2), deckH + 0.1, 0);
      group.add(sideTrim);
    }

    /* ── Backwall ── */
    const backWallH = 22;
    const backWall = new THREE.Mesh(
      new THREE.BoxGeometry(deckW, backWallH, 0.4),
      matSteel
    );
    backWall.position.set(0, deckH + backWallH / 2, -deckD / 2 + 0.2);
    backWall.castShadow = true;
    group.add(backWall);

    /* ── Truss columns (front pair) ── */
    const trussH = 26;
    const colCount = 2;
    for (let i = 0; i < colCount; i++) {
      const x = i === 0 ? -deckW / 2 + 1.5 : deckW / 2 - 1.5;
      // Box truss with diagonal cross-bracing visual
      const truss = new THREE.Mesh(
        new THREE.BoxGeometry(0.8, trussH, 0.8),
        matTruss
      );
      truss.position.set(x, deckH + trussH / 2, deckD / 2 - 1);
      truss.castShadow = true;
      group.add(truss);
      // Cross braces along the height
      for (let h = 1; h < 5; h++) {
        const brace = new THREE.Mesh(
          new THREE.CylinderGeometry(0.05, 0.05, 1.4, 4),
          matTruss
        );
        brace.position.set(x, deckH + h * (trussH / 5), deckD / 2 - 1);
        brace.rotation.z = h % 2 === 0 ? 0.7 : -0.7;
        group.add(brace);
      }
    }

    /* ── Top horizontal truss spanning front pair ── */
    const topTruss = new THREE.Mesh(
      new THREE.BoxGeometry(deckW - 2, 0.8, 0.8),
      matTruss
    );
    topTruss.position.set(0, deckH + trussH, deckD / 2 - 1);
    topTruss.castShadow = true;
    group.add(topTruss);

    /* ── CURVED ROOF — clean tube along an arc ── */
    // Big sweeping half-arc from one front truss to the other.
    const roofRise = 6;
    const roofPath = new THREE.CatmullRomCurve3([
      new THREE.Vector3(-deckW / 2 + 1.5, trussH + deckH, deckD / 2 - 1),
      new THREE.Vector3(-deckW / 4, trussH + deckH + roofRise * 0.55, deckD / 4),
      new THREE.Vector3(0, trussH + deckH + roofRise, 0),
      new THREE.Vector3(deckW / 4, trussH + deckH + roofRise * 0.55, deckD / 4),
      new THREE.Vector3(deckW / 2 - 1.5, trussH + deckH, deckD / 2 - 1)
    ]);

    // Roof TUBE — looks like a bent steel beam
    const roofTubeGeo = new THREE.TubeGeometry(roofPath, 32, 0.55, 12, false);
    const roofTube = new THREE.Mesh(roofTubeGeo, matSteel);
    roofTube.castShadow = true;
    group.add(roofTube);

    // Second tube at the back of the roof for depth
    const roofPathBack = new THREE.CatmullRomCurve3(
      roofPath.points.map((p) => new THREE.Vector3(p.x, p.y, p.z - (deckD - 2)))
    );
    const roofTubeBack = new THREE.Mesh(
      new THREE.TubeGeometry(roofPathBack, 32, 0.55, 12, false),
      matSteel
    );
    roofTubeBack.castShadow = true;
    group.add(roofTubeBack);

    // Roof CANOPY between the two arcs — a curved ribbon
    const canopyShape = new THREE.Shape();
    canopyShape.moveTo(-deckW / 2 + 1.5, deckH + trussH);
    for (let i = 1; i <= 32; i++) {
      const t = i / 32;
      const p = roofPath.getPoint(t);
      canopyShape.lineTo(p.x, p.y);
    }
    canopyShape.lineTo(deckW / 2 - 1.5, deckH + trussH - 0.05);
    canopyShape.lineTo(-deckW / 2 + 1.5, deckH + trussH - 0.05);
    const canopyGeo = new THREE.ExtrudeGeometry(canopyShape, {
      depth: deckD - 2,
      bevelEnabled: false,
      curveSegments: 24
    });
    canopyGeo.translate(0, 0, -(deckD - 2));
    canopyGeo.translate(0, 0, deckD / 2 - 1);
    const canopy = new THREE.Mesh(canopyGeo, matSteel);
    canopy.castShadow = true;
    canopy.receiveShadow = true;
    group.add(canopy);

    // Connecting ribs every 4m
    for (let i = 1; i <= 6; i++) {
      const t = i / 7;
      const f = roofPath.getPoint(t);
      const b = roofPathBack.getPoint(t);
      const ribDir = new THREE.Vector3().subVectors(b, f);
      const len = ribDir.length();
      const rib = new THREE.Mesh(
        new THREE.CylinderGeometry(0.12, 0.12, len, 6),
        matTruss
      );
      const mid = new THREE.Vector3().addVectors(f, b).multiplyScalar(0.5);
      rib.position.copy(mid);
      rib.lookAt(b);
      rib.rotateX(Math.PI / 2);
      group.add(rib);
    }

    /* ── Twin jumbotron screens flanking the deck ── */
    const screenW = 9;
    const screenH = 5;
    for (const sign of [-1, 1] as const) {
      const xPos = sign * (deckW / 2 + 4);
      const yPos = deckH + screenH / 2 + 4;
      const zPos = deckD / 2 - 1;

      const frame = new THREE.Mesh(
        new THREE.BoxGeometry(screenW + 0.6, screenH + 0.6, 0.5),
        matScreenFrame
      );
      frame.position.set(xPos, yPos, zPos);
      group.add(frame);

      const screen = new THREE.Mesh(
        new THREE.PlaneGeometry(screenW, screenH),
        matScreen
      );
      screen.position.set(xPos, yPos, zPos + 0.27);
      group.add(screen);

      // Side support pole
      const pole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.25, 0.3, deckH + screenH + 4, 8),
        matTruss
      );
      pole.position.set(xPos, (deckH + screenH + 4) / 2, zPos);
      pole.castShadow = true;
      group.add(pole);
    }

    /* ── Center main backdrop screen ── */
    const mainW = 18;
    const mainH = 10;
    const mainFrame = new THREE.Mesh(
      new THREE.BoxGeometry(mainW + 0.8, mainH + 0.8, 0.4),
      matScreenFrame
    );
    mainFrame.position.set(0, deckH + mainH / 2 + 3, -deckD / 2 + 0.6);
    group.add(mainFrame);

    const mainScreen = new THREE.Mesh(
      new THREE.PlaneGeometry(mainW, mainH),
      matScreen
    );
    mainScreen.position.set(0, deckH + mainH / 2 + 3, -deckD / 2 + 0.85);
    mainScreen.userData.screenRole = 'main';
    group.add(mainScreen);

    /* ── Speaker stacks ── */
    for (const sign of [-1, 1] as const) {
      const stackX = sign * (deckW / 2 - 3);
      for (let s = 0; s < 4; s++) {
        const speaker = new THREE.Mesh(
          new THREE.BoxGeometry(2.4, 1.2, 1.6),
          matSpeaker
        );
        speaker.position.set(stackX, deckH + 0.6 + s * 1.25, deckD / 2 - 0.5);
        speaker.castShadow = true;
        group.add(speaker);
        // Speaker grill (small detail)
        const grill = new THREE.Mesh(
          new THREE.CircleGeometry(0.45, 12),
          new THREE.MeshStandardMaterial({ color: '#0a0a0e', roughness: 0.9 })
        );
        grill.position.set(stackX, deckH + 0.6 + s * 1.25, deckD / 2 + 0.31);
        group.add(grill);
      }
    }

    /* ── Hanging stage lights (LED accents) ── */
    for (let i = 0; i < 9; i++) {
      const t = i / 8;
      const x = -deckW / 2 + 2 + t * (deckW - 4);
      const lamp = new THREE.Mesh(
        new THREE.SphereGeometry(0.22, 8, 6),
        i % 2 === 0 ? matLed : matTrim
      );
      lamp.position.set(x, deckH + trussH - 0.5, deckD / 2 - 1.2);
      group.add(lamp);
    }

    /* ── Audio anchor in front of the deck ── */
    const audioAnchor = new THREE.Object3D();
    audioAnchor.position.set(0, 5, deckD / 2 + 6);
    group.add(audioAnchor);

    return {
      id: 'what-stage',
      label: 'What Stage',
      group,
      position: new THREE.Vector3(),
      audioAnchor,
      audioFalloff: { ref: 60, max: 320 },
      screenQuad: makeScreenQuad(
        new THREE.Vector3(0, deckH + mainH / 2 + 3, -deckD / 2 + 0.85),
        mainW,
        mainH,
        Math.PI
      ),
      screenWidth: mainW,
      screenHeight: mainH
    };
  }
}
